import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcZoneDto {
    id: number;
    companyNo: string;
    warehouseID: string;
    stcZoneID: string;
    description1: string;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcZoneRequest {
    companyNo: string;
    warehouseID: string;
    stcZoneID: string;
    description1: string;
}

export interface UpdateStcZoneRequest {
    description1: string;
}

export interface BulkUpdateStcZoneRequest {
    companyNo: string;
    warehouseID: string;
    stcZoneID: string;
    description1: string;
}

class StcZoneService {
    private baseUrl = `${API_BASE_URL}/stc-zones`;

    /**
     * Get zone by company, warehouse and zone ID
     */
    async getByCompanyWarehouseAndZoneId(companyNo: string, warehouseID: string, stcZoneID: string): Promise<StcZoneDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${warehouseID}/${stcZoneID}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Get all zones for a company and warehouse
     */
    async getByCompanyAndWarehouse(companyNo: string, warehouseID: string): Promise<StcZoneDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${warehouseID}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get all zones for a company
     */
    async getByCompanyNo(companyNo: string): Promise<StcZoneDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get all zones for current tenant
     */
    async getCurrent(): Promise<StcZoneDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get all zones
     */
    async getAll(): Promise<StcZoneDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Create new zone
     */
    async create(request: CreateStcZoneRequest): Promise<StcZoneDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Update zone
     */
    async update(companyNo: string, warehouseID: string, stcZoneID: string, request: UpdateStcZoneRequest): Promise<StcZoneDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${warehouseID}/${stcZoneID}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Bulk update zones
     */
    async bulkUpdate(requests: BulkUpdateStcZoneRequest[]): Promise<StcZoneDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Delete zone
     */
    async delete(companyNo: string, warehouseID: string, stcZoneID: string): Promise<boolean> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${warehouseID}/${stcZoneID}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return true;
    }

    /**
     * Bulk delete zones by IDs
     */
    async bulkDelete(ids: number[]): Promise<any> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }
}

export const stcZoneService = new StcZoneService();
